import React, { useEffect, useState } from 'react';
import api, { STATIC_BASE_URL } from '../api';
import BackButton from '../components/BackButton';

export default function LogoPage() {
  const [logo, setLogo] = useState(null);
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');

  // Always get correct logo URL
  function getLogoUrl(fileName) {
    if (!fileName) return '';
    return `${STATIC_BASE_URL}/uploads/logos/${fileName}`;
  }

  // Fetch current logo on load
  const fetchLogo = async () => {
    try {
      const res = await api.get('/logo/current');
      setLogo(res.data.logo);
    } catch {
      setLogo(null);
    }
  };

  useEffect(() => {
    fetchLogo();
  }, []);

  // Handle file select
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  // Upload new logo
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setMessage('');
    setCopySuccess('');
    try {
      const formData = new FormData();
      formData.append('logo', file);
      await api.post('/logo/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMessage('✅ Logo uploaded!');
      setFile(null);
      fetchLogo();
    } catch (err) {
      setMessage('❌ Error uploading logo.');
    }
    setUploading(false);
  };

  // Delete logo
  const handleDelete = async () => {
    if (!logo) return;
    if (!window.confirm('Are you sure you want to delete this logo?')) return;
    try {
      await api.delete(`/logo/${logo.LogoID}`);
      setMessage('✅ Logo deleted.');
      setLogo(null);
    } catch {
      setMessage('❌ Error deleting logo.');
    }
  };

  // Copy path to clipboard
  const handleCopyPath = () => {
    const path = getLogoUrl(logo.FileName);
    navigator.clipboard.writeText(path);
    setCopySuccess('Copied!');
    setTimeout(() => setCopySuccess(''), 1200);
  };

  // Glass card style
  const cardStyle = {
    background: 'rgba(255,255,255,0.97)',
    borderRadius: '2.5rem',
    boxShadow: '0 8px 48px 0 rgba(40, 110, 250, 0.10)',
    backdropFilter: 'blur(9px)',
    border: '1.5px solid rgba(180,210,255,0.18)',
    margin: '0 auto',
    maxWidth: 480
  };

  return (
    <div
      className="min-vh-100 py-5"
      style={{
        background: 'linear-gradient(120deg, #b3d8fc 0%, #e8f1ff 100%)',
        minHeight: '100vh',
      }}
    >
      <div className="container" style={{ maxWidth: 550 }}>
        <div className="d-flex align-items-center my-3">
          <BackButton />
          <h3 className="ms-2 mb-0 fw-bold" style={{ letterSpacing: 1, color: '#2254a0' }}>Manage Logo</h3>
        </div>
        <div className="card shadow-lg rounded-5 p-4 border-0" style={cardStyle}>
          <div className="mb-4 text-center">
            <div className="fw-semibold mb-2" style={{ color: '#3459b7' }}>Current Logo</div>
            {logo && logo.FileName ? (
              <div>
                <img
                  src={getLogoUrl(logo.FileName)}
                  alt="Logo"
                  className="img-fluid rounded border mb-2"
                  style={{ maxHeight: 140, background: '#f7fbff', boxShadow: '0 2px 16px #dae6fa33' }}
                />
                {/* Copyable Logo Path */}
                <div className="mt-2 mb-2">
                  <div className="input-group mb-1" style={{ maxWidth: 350, margin: '0 auto' }}>
                    <input
                      type="text"
                      className="form-control rounded-pill px-2"
                      value={getLogoUrl(logo.FileName)}
                      readOnly
                      style={{ background: '#f4faff', fontSize: 13 }}
                      onFocus={e => e.target.select()}
                    />
                    <button
                      className="btn btn-outline-secondary rounded-pill ms-2 px-3"
                      type="button"
                      onClick={handleCopyPath}
                      style={{ fontSize: 13 }}
                    >
                      Copy Path
                    </button>
                  </div>
                  <div style={{ fontSize: 12, color: '#4477cc' }}>
                    {copySuccess ? <span className="fw-semibold text-success">{copySuccess}</span> : <>Copy this path and paste in the Company Settings "Logo URL" field.</>}
                  </div>
                </div>
                <div className="mt-2">
                  <button className="btn btn-outline-danger rounded-pill px-4 py-1" onClick={handleDelete}>
                    Delete Logo
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-muted" style={{ fontSize: 15 }}>No logo uploaded.</div>
            )}
          </div>
          <hr />
          <form onSubmit={handleUpload} className="text-center">
            <label className="form-label fw-semibold">Upload New Logo (PNG/JPG, recommended height 80-150px)</label>
            <input
              type="file"
              accept="image/*"
              className="form-control rounded-pill mb-2"
              onChange={handleFileChange}
              style={{ maxWidth: 310, margin: '0 auto' }}
            />
            <button className="btn btn-primary rounded-pill px-4 py-2 fw-semibold" type="submit" disabled={uploading || !file}>
              {uploading ? 'Uploading...' : 'Upload Logo'}
            </button>
          </form>
          {message && <div className="mt-3 text-center fw-semibold text-success">{message}</div>}
        </div>
      </div>
    </div>
  );
}
